import { CountBird, Icon, type IconName, LINK_CLASS } from '@league/ui';
import { clsx } from 'clsx';
import { Link, NavLink, Outlet } from 'react-router-dom';

import type { Actor } from '../lib/auth';
import { PageErrorBoundary } from '../components/PageErrorBoundary';

/**
 * The player app. **Three tabs. Never a fourth.** — UI-UX-SPEC §3.2, and the constraint is the
 * design: a player opens this standing in a room five minutes before play, wanting to know where
 * to sit. Anything that isn't Today, Standings or You belongs inside one of them.
 *
 * No hamburger and no settings gear either. Settings live inside You, because a gear icon in a
 * three-tab app is a fourth tab wearing a disguise.
 *
 * Bottom bar on a phone, top bar from 768px. Bottom because that is where a thumb is, and because
 * a player uses this one-handed while holding a rack of tiles.
 */
const TABS: { to: string; label: string; icon: IconName }[] = [
  { to: '/my', label: 'My League', icon: 'today' },
  { to: '/my/standings', label: 'Standings', icon: 'standings' },
  { to: '/my/you', label: 'My Profile', icon: 'you' },
];

export function PlayerShell({ actor, brand }: { actor: Actor; brand: string }) {
  return (
    <div className="flex min-h-screen flex-col">
      <TabBar position="top" brand={brand} />

      {/*
        The bottom bar is fixed, so the last thing on the page would sit under it. `pb-24` on a
        phone is the bar plus a thumb's worth of room — a Sign out button you cannot reach because
        the tab bar is on top of it is a genuinely common bug. The page itself scrolls, as on every
        other screen, so the phone's own scrolling (the address bar tucking away, tap-the-top) works.
      */}
      <main className="min-w-0 flex-1 pb-24 md:pb-10">
        {/*
          A DIRECTOR WHO PLAYS came here from "My games" (owner, 2026-09-26), so she needs the way
          back. A strip above the page rather than a fourth tab: the three tabs are the player app,
          and this is a door out of it, which only staff have.
        */}
        {actor.role !== 'player' ? (
          <div className="border-b border-light bg-sand">
            <div className="mx-auto w-full max-w-2xl px-5 py-2">
              <Link
                to={actor.role === 'commissioner' ? '/hq' : '/director'}
                className={`inline-flex min-h-[44px] items-center text-[14px] ${LINK_CLASS}`}
              >
                ← Back to your {actor.role === 'commissioner' ? 'HQ' : 'director'} dashboard
              </Link>
            </div>
          </div>
        ) : null}
        <PageErrorBoundary><Outlet context={actor} /></PageErrorBoundary>
      </main>

      <TabBar position="bottom" brand={brand} />
    </div>
  );
}

function TabBar({ position, brand }: { position: 'top' | 'bottom'; brand: string }) {
  const isTop = position === 'top';

  return (
    <nav
      aria-label="Sections"
      className={clsx(
        'border-light bg-paper',
        isTop
          ? 'hidden border-b md:block'
          : /*
             * `pb-[env(safe-area-inset-bottom)]` keeps the tabs clear of the home indicator on an
             * iPhone. Without it the last few pixels of every tab are untappable.
             */
            'fixed inset-x-0 bottom-0 z-20 border-t pb-[env(safe-area-inset-bottom)] md:hidden',
      )}
    >
      <div
        className={clsx(
          'mx-auto flex w-full max-w-[1200px] items-center',
          isTop ? 'gap-8 px-10 py-3' : 'justify-around px-2 py-1.5',
        )}
      >
        {/*
          The same mark the public site, the sign-in card and the staff rails carry. The name on its
          own was the odd one out: a player who registered on the site and signed in here saw the
          mark disappear at exactly the point she started trusting the thing with her evenings.

          `light`, not `navy` — this bar is `bg-paper`. The rendition follows the GROUND, never the
          page, which is the whole rule in the brand assets README.

          Top bar only. The bottom bar is four thumb targets on a phone and has no room for a logo.
        */}
        {isTop ? (
          <span className="mr-2 flex items-center gap-2">
            {/*
              72, not 44 — owner's call, 2026-08-30, so the full 1-5-2 tail shows here too. This is
              a top bar on a phone, so it is the tightest place the full mark now has to fit.
            */}
            <CountBird size={72} ground="light" className="shrink-0" />
            <span className="font-display text-lg leading-none">{brand}</span>
          </span>
        ) : null}

        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === '/my'}
            className={({ isActive }) =>
              clsx(
                'flex items-center transition-colors',
                isTop
                  ? 'gap-2 text-[14px]'
                  : /* 56px tall and equal width, so every tab is a comfortable thumb target. */
                    'min-w-[72px] flex-col gap-1 rounded px-3 py-1.5 text-[11px]',
                isActive ? 'font-semibold text-deep' : 'text-ink-2 hover:text-ink',
              )
            }
          >
            <Icon name={tab.icon} size={isTop ? 17 : 21} />
            {tab.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
